import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Card, CardContent, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import useSubscriber, { OnlyFansSubscriber } from '../../../hooks/useSubscriber';
import useTtsReferenceModels, { OnlyStrugglesTtsProviders } from '../../../hooks/useTtsReferenceModels';
import { UserContext } from '../../../store/UserContext';
import {
    createEmptyOnlyFansSubscriberMessageUsersTtsSettings,
    OnlyFansSubscriberMessageUsersTtsSettings,
} from '../../../types/messageUsersAutoSendWelcomeMessage';
import ElevenLabsApiKeyDialog from './ElevenLabsApiKeyDialog';
import ElevenLabsVoiceSettingsEditor from './ElevenLabsVoiceSettingsEditor';
import TtsElevenLabsVoiceModelSelector from './TtsElevenLabsVoiceModelSelector';
import TtsOnlyStrugglesVoiceModelSelector from './TtsOnlyStrugglesVoiceModelSelector';
import TtsProviderSelector from './TtsProviderSelector';

type Props = {
    subscriber: OnlyFansSubscriber;
    ttsSettings: OnlyFansSubscriberMessageUsersTtsSettings;
    setTtsSettings: React.Dispatch<React.SetStateAction<OnlyFansSubscriberMessageUsersTtsSettings>>;
    disabled?: boolean;
};

const TtsMessageSettingsCard = (props: Props) => {
    const { subscriber, ttsSettings, setTtsSettings, disabled } = props;
    const userContext = useContext(UserContext);
    const { enqueueSnackbar } = useSnackbar();

    const { ttsGenerateTest, ttsGenerateTestLoading } = useTtsReferenceModels();

    const [error, setError] = useState<string | null>(null);
    const [testAudioURL, setTestAudioURL] = useState<string | null>(ttsSettings?.generatedFilename?.url || null);
    const [hasChanges, setHasChanges] = useState(ttsSettings?.generatedFilename ? false : true);
    const [lastGeneratedSettings, setLastGeneratedSettings] = useState<OnlyFansSubscriberMessageUsersTtsSettings | null>(ttsSettings);

    const { setServiceSettings, setServiceRunningLoading } = useSubscriber();

    // Eleven Labs
    const [openElevenLabsApiKeyDialog, setOpenElevenLabsApiKeyDialog] = useState(false);
    const handleOpenElevenLabsApiKeyDialog = () => {
        setOpenElevenLabsApiKeyDialog(true);
    };
    const handleCloseElevenLabsApiKeyDialog = () => {
        setOpenElevenLabsApiKeyDialog(false);
    };
    const handleSaveElevenLabsApiKey = (apiKey: string) => {
        // Save the API key to the subscriber's messageUsers settings
        setServiceSettings('messageUsers.elevenLabsApiKey', { apiKey })
            .then(() => {
                enqueueSnackbar('ElevenLabs API Key saved successfully', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });

                // Close the dialog
                handleCloseElevenLabsApiKeyDialog();
            })
            .catch(error => {
                enqueueSnackbar(`Failed to save ElevenLabs API Key: ${error.message}`, {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });
    };

    // Track changes in settings
    useEffect(() => {
        if (!ttsSettings || !ttsSettings.generatedFilename) {
            setHasChanges(true);
            return;
        }

        if (!lastGeneratedSettings) {
            setHasChanges(false);
            return;
        }

        const settingsChanged =
            ttsSettings.provider !== lastGeneratedSettings.provider ||
            ttsSettings.modelId !== lastGeneratedSettings.modelId ||
            ttsSettings.message !== lastGeneratedSettings.message ||
            ttsSettings.addUsername !== lastGeneratedSettings.addUsername ||
            ttsSettings.usernameGreeting !== lastGeneratedSettings.usernameGreeting ||
            JSON.stringify(ttsSettings.voiceSettings || {}) !== JSON.stringify(lastGeneratedSettings.voiceSettings || {});

        setHasChanges(settingsChanged);
    }, [ttsSettings, lastGeneratedSettings]);

    const handleTestPrompt = async () => {
        if (
            !ttsSettings.provider ||
            !ttsSettings.modelId ||
            !ttsSettings.message?.trim() ||
            (ttsSettings.addUsername && !ttsSettings.usernameGreeting)
        ) {
            setError('Please fill in all required fields');
            setTestAudioURL(null);

            return;
        }

        try {
            const response = await ttsGenerateTest(ttsSettings.modelId, ttsSettings.message, ttsSettings.provider);

            if (response && response.success && response.url) {
                // In a real implementation, you'd fetch the audio from S3
                // This is just a placeholder
                setTestAudioURL(response.url);
                // Store the settings that were used to generate this audio
                setLastGeneratedSettings({ ...ttsSettings });
                // Save the fileName to the settings
                setTtsSettings(prevState => {
                    return {
                        ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                        generatedFilename: {
                            url: response.url,
                            fileName: response.fileName,
                        },
                    };
                });
                setHasChanges(false);
            } else {
                setTestAudioURL(null);
            }
        } catch (err) {
            setError('Failed to generate test audio');
            setTestAudioURL(null);
        }
    };

    return (
        <Card variant="elevation" sx={{ mb: 1 }}>
            <CardContent>
                <Stack direction="column" spacing={1}>
                    <div>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="medium"
                                    checked={ttsSettings && ttsSettings.active ? true : false}
                                    disabled={disabled}
                                    onChange={(e, checked) => {
                                        setTtsSettings(prevState => {
                                            return {
                                                ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                active: checked,
                                            };
                                        });
                                    }}
                                />
                            }
                            label={<Typography variant="h6">Auto Voice Note (BETA)</Typography>}
                        />
                    </div>
                    <Typography variant="body1">
                        Send an automatically generated voice note to the fan with their name in your own voice!
                    </Typography>

                    {ttsSettings && ttsSettings.active && (
                        <Stack direction="column" spacing={2}>
                            <Stack direction="column" spacing={1}>
                                <Typography variant="body2" color={'text.secondary'}>
                                    Select the TTS provider you want to use for sending voice notes. The OnlyStruggles service is free and
                                    will sound just like you, but the Eleven Labs service is paid and will sound more realistic.
                                </Typography>
                                <TtsProviderSelector
                                    disabled={disabled}
                                    ttsProvider={ttsSettings.provider}
                                    handleTtsProviderChange={(provider: string) => {
                                        setTtsSettings(prevState => {
                                            return {
                                                ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                provider: provider as OnlyStrugglesTtsProviders,
                                                ...(provider === 'onlystruggles' ? { voiceSettings: undefined } : {}),
                                            };
                                        });
                                    }}
                                />
                            </Stack>
                            {ttsSettings.provider === 'onlystruggles' && (
                                <Stack direction="column" spacing={1}>
                                    <Typography variant="body2" color={'text.secondary'}>
                                        Select the TTS model you want to use for generating the voice notes
                                    </Typography>
                                    <TtsOnlyStrugglesVoiceModelSelector
                                        ttsModelId={ttsSettings.modelId}
                                        handleTtsModelIdChange={modelId => {
                                            setTtsSettings(prevState => {
                                                return {
                                                    ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                    modelId: modelId,
                                                };
                                            });
                                        }}
                                        size="small"
                                    />
                                </Stack>
                            )}
                            {ttsSettings.provider === 'elevenlabs' && (
                                <Stack direction="column" spacing={1}>
                                    <Typography variant="body2" color={'text.secondary'}>
                                        Select the Eleven Labs TTS voice you want to use for generating the voice notes.
                                    </Typography>
                                    <LoadingButton
                                        variant={subscriber.messageUsers?.elevenLabsApiKey ? 'outlined' : 'contained'}
                                        size="medium"
                                        disabled={disabled}
                                        loading={setServiceRunningLoading}
                                        onClick={() => {
                                            handleOpenElevenLabsApiKeyDialog();
                                        }}
                                        sx={{ mb: 1 }}
                                    >
                                        <span>{subscriber.messageUsers?.elevenLabsApiKey ? 'Edit' : 'Setup'} Eleven Labs API Key</span>
                                    </LoadingButton>
                                    <ElevenLabsApiKeyDialog
                                        open={openElevenLabsApiKeyDialog}
                                        apiKey={subscriber.messageUsers?.elevenLabsApiKey || ''}
                                        onClose={handleCloseElevenLabsApiKeyDialog}
                                        onSave={handleSaveElevenLabsApiKey}
                                    />
                                    {!subscriber.messageUsers?.elevenLabsApiKey && (
                                        <>
                                            <Typography variant="body2" color={'text.secondary'}>
                                                You need to set up an Eleven Labs account and get your API key to use this feature.
                                                <br />
                                                <strong>Please note that you will be charged by Eleven Labs for the TTS generation.</strong>
                                            </Typography>
                                        </>
                                    )}
                                    {subscriber.messageUsers?.elevenLabsApiKey && (
                                        <TtsElevenLabsVoiceModelSelector
                                            elevenLabsApiKey={subscriber.messageUsers.elevenLabsApiKey}
                                            ttsModelId={ttsSettings.modelId}
                                            disabled={disabled}
                                            handleTtsModelIdChange={modelId => {
                                                setTtsSettings(prevState => {
                                                    return {
                                                        ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                        modelId: modelId,
                                                    };
                                                });
                                            }}
                                            size="small"
                                        />
                                    )}
                                </Stack>
                            )}
                            <Stack direction="column" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="medium"
                                            checked={ttsSettings && ttsSettings.addUsername ? true : false}
                                            disabled={disabled}
                                            onChange={(e, checked) => {
                                                setTtsSettings(prevState => {
                                                    return {
                                                        ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                        addUsername: checked,
                                                    };
                                                });
                                            }}
                                            sx={{
                                                p: 0,
                                                mr: 1,
                                            }}
                                        />
                                    }
                                    label={<Typography variant="body1">Add the fan's name for a personal greeting?</Typography>}
                                />
                                <Typography variant="body2" color={'text.secondary'} gutterBottom>
                                    If enabled, the system will try to work out the fan's name from their profile so it sounds real and
                                    personal.
                                </Typography>
                            </Stack>
                            {ttsSettings.addUsername && (
                                <Stack direction="column" spacing={1}>
                                    <Typography variant="body2" color={'text.secondary'}>
                                        Add a custom greeting to the fan's name? This will be added to the start of the message.{' '}
                                        <strong>
                                            Do not include the actual name below! Just the greeting you would say before the name.
                                        </strong>
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        disabled={disabled}
                                        value={ttsSettings.usernameGreeting || ''}
                                        onChange={e => {
                                            setTtsSettings(prevState => {
                                                return {
                                                    ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                    usernameGreeting: e.target.value,
                                                };
                                            });
                                        }}
                                        placeholder="e.g. Heyy"
                                        error={
                                            (ttsSettings.usernameGreeting?.trim() || '').length === 0 ||
                                            (ttsSettings.usernameGreeting || '').length > 40
                                        }
                                        helperText={`${(ttsSettings.usernameGreeting || '').length} / 40 characters`}
                                    />
                                </Stack>
                            )}

                            <Stack direction="column" spacing={1}>
                                <Typography variant="body2" color={'text.secondary'}>
                                    The voice message to send to the fan. This will be attached to the message and will be read out in your
                                    voice. Keep it short - no one will believe that you recorded a long message just for one fan! - sexy and
                                    welcoming. But also keep it generic so it applies to all fans.
                                </Typography>
                                <Typography variant="body2" color={'text.secondary'}>
                                    <strong>
                                        Do not include the actual name in the message! Just the message you want to send. No emojies,
                                        abbreviations or special characters. Punctuation matters! So do new lines, three dots, and
                                        capitalization. They affect the generated audio.
                                    </strong>
                                </Typography>
                                <Typography variant="body2" color={'text.secondary'}>
                                    Click the "Generate Audio" button to generate a preview of the audio. Results depend heavily on the
                                    quality and tone of the sample you trained the model on! If you want your message to sound more happy,
                                    record a happier sample. If you want it to sound more natural and breathy and giggly, record a sample
                                    with those traits. You can then listen to it and adjust the message if needed. Once you are happy with
                                    the audio, it will be saved and used for all new fans. Every time you change the message, you will need
                                    to generate a new audio file. AI generations are random. You might want to try a couple of times to get
                                    the best result.
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    disabled={disabled}
                                    value={ttsSettings.message || ''}
                                    onChange={e => {
                                        if (e.target.value.length <= 300) {
                                            setTtsSettings(prevState => {
                                                return {
                                                    ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                    message: e.target.value,
                                                };
                                            });
                                        }
                                    }}
                                    multiline
                                    rows={4}
                                    margin="normal"
                                    placeholder="I would do anything for love... but I won't do that."
                                    inputProps={{ maxLength: 300 }}
                                    helperText={`${(ttsSettings.message || '').length} / 300 characters`}
                                    error={(ttsSettings.message || '').length > 300 || (ttsSettings.message || '').length === 0}
                                />
                            </Stack>

                            {ttsSettings.provider === 'elevenlabs' && (
                                <ElevenLabsVoiceSettingsEditor
                                    voiceSettings={ttsSettings.voiceSettings || null}
                                    onChange={newVoiceSettings => {
                                        setTtsSettings(prevState => {
                                            return {
                                                ...(prevState || createEmptyOnlyFansSubscriberMessageUsersTtsSettings()),
                                                voiceSettings: newVoiceSettings,
                                            };
                                        });
                                    }}
                                    disabled={disabled}
                                />
                            )}

                            <LoadingButton
                                variant="contained"
                                color={hasChanges ? 'warning' : 'primary'}
                                size="large"
                                loading={ttsGenerateTestLoading}
                                disabled={!ttsSettings.message?.trim() || !ttsSettings.modelId || disabled}
                                onClick={handleTestPrompt}
                                sx={{ mt: 2 }}
                            >
                                <span>Generate Audio</span>
                            </LoadingButton>

                            {testAudioURL && (
                                <div>
                                    <Typography variant="body2" gutterBottom>
                                        Preview:
                                    </Typography>
                                    <audio
                                        controls
                                        src={testAudioURL}
                                        // no download attribute to prevent download
                                        controlsList="nodownload"
                                        style={{ width: '100%' }}
                                        onContextMenu={e => {
                                            if (!userContext.isAdmin) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </div>
                            )}

                            {error && (
                                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default TtsMessageSettingsCard;
